/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useRef} from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'
import RTE from '@consteed/frontend/components/pageDesigner/RTE'
import {motion, useScroll, useTransform} from 'framer-motion'

/**
 * GeneralVideo component
 *
 * @param {object} props
 * @param {string} - props.videoURL - video URL
 * @param {string} - props.mobileVideoURL - video URL for smartphones
 * @param {image} - props.placeholderImage - placeholder image for video
 * @param {boolean} - props.autoplay - autoplay flag for video
 * @param {boolean} - props.showControls - should the video controls be displayed or not
 * @param {string} - props.text - text
 * @param {string} - props.backgroundTheme - defines the theme of the background (which has impact on the text color of the overlay)
 * @returns {React.ReactElement} - GeneralVideo component.
 */
export const GeneralVideo = ({
    videoURL,
    mobileVideoURL,
    placeholderImage,
    autoplay = false,
    loop = false,
    showControls = false,
    text = '',
    backgroundTheme = 'transparent/black'
}) => {
    const textColor = backgroundTheme === 'transparent/black' ? 'white' : 'black'
    const styles = useMultiStyleConfig('GeneralVideo', {
        backgroundTheme,
        textColor
    })

    /* animation config for video */
    const refVideo = useRef(null)
    const {scrollYProgress: scrollYProgressVideo} = useScroll({
        target: refVideo,
        offset: ['start end', '0.9 1']
    })
    const scaleProgressVideo = useTransform(scrollYProgressVideo, [0, 1], [0.7, 1])

    /* animation config for text */
    const refText = useRef(null)
    const {scrollYProgress: scrollYProgressText} = useScroll({
        target: refText,
        offset: ['start end', '0.9 1']
    })
    const opacityProgressText = useTransform(scrollYProgressText, [0, 1], [0, 1])

    return (
        <React.Fragment>
            <Box className={'pdcOuterBox pdcFullWidth'} {...styles.backgroundContainer}>
                <motion.div
                    ref={refVideo}
                    style={{
                        scale: scaleProgressVideo
                    }}
                >
                    <Box className="videoContainer" {...styles.videoContainer}>
                        <video
                            autoPlay={autoplay}
                            loop={loop}
                            poster={placeholderImage?.url}
                            controls={showControls}
                            muted
                            style={{borderRadius: '30px'}}
                            playsInline={true}
                        >
                            {mobileVideoURL && (
                                <source
                                    src={mobileVideoURL}
                                    media="(max-width:767px)"
                                    type="video/mp4"
                                />
                            )}
                            <source src={videoURL} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    </Box>
                </motion.div>
            </Box>
            <Box {...styles.outerTextContainer}>
                <motion.div
                    ref={refText}
                    style={{
                        opacity: opacityProgressText
                    }}
                >
                    <Flex justifyContent="end" {...styles.textContainer}>
                        <RTE
                            richText={text}
                            textStyles={{base: 'bookLarge', xl: 'book2Xlarge'}}
                            isNested={true}
                            width={{lg: '75%'}}
                            margin={0}
                        />
                    </Flex>
                </motion.div>
            </Box>
        </React.Fragment>
    )
}

GeneralVideo.propTypes = {
    videoURL: PropTypes.string,
    mobileVideoURL: PropTypes.string,
    placeholderImage: PropTypes.shape({
        _type: PropTypes.string,
        focalPoint: PropTypes.shape({
            _type: PropTypes.string,
            x: PropTypes.number,
            y: PropTypes.number
        }),
        metaData: PropTypes.shape({
            _type: PropTypes.string,
            height: PropTypes.number,
            width: PropTypes.number
        }),
        url: PropTypes.string,
        src: PropTypes.string || PropTypes.object
    }).isRequired,
    autoplay: PropTypes.bool,
    loop: PropTypes.bool,
    showControls: PropTypes.bool,
    text: PropTypes.string.isRequired,
    backgroundTheme: PropTypes.oneOf(['transparent', 'transparent/black', 'black/transparent'])
}

export default GeneralVideo
